/**
 * TriggersView -- the user-definable trigger rule builder (P349).
 *
 * Route: /admin/triggers. Metric (closed catalogue) + comparator +
 * threshold -> edge-triggered notification across every channel.
 */
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "shared/api/client";
import { MarsadIcon } from "shared/components/MarsadIcon";

interface MetricSpec {
  key: string;
  label: string;
  unit: string;
}

interface TriggerRule {
  id: string;
  name: string;
  /** P421 -- "event" rules watch for a significant change, not a number. */
  rule_kind?: "metric" | "event";
  event_kind?: string | null;
  metric_key: string | null;
  comparator: string | null;
  threshold: string | null;
  severity: string;
  ack_required: boolean;
  enabled: boolean;
  last_state: string;
  last_value: string | null;
  last_evaluated_at: string | null;
  last_fired_at: string | null;
}

const COMPARATOR_LABEL: Record<string, string> = {
  gt: ">",
  gte: ">=",
  lt: "<",
  lte: "<=",
};

export function TriggersView(): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const catalogue = useQuery({
    queryKey: ["admin", "trigger-catalogue"],
    queryFn: () => api.get<MetricSpec[]>("/v1/admin/twin/triggers/catalogue"),
    staleTime: 5 * 60_000,
  });
  const rules = useQuery({
    queryKey: ["admin", "trigger-rules"],
    queryFn: () => api.get<TriggerRule[]>("/v1/admin/twin/triggers"),
    refetchInterval: 30_000,
  });
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["admin", "trigger-rules"] });

  const [name, setName] = useState("");
  const [metricKey, setMetricKey] = useState("open_critical_findings");
  const [comparator, setComparator] = useState("gt");
  const [threshold, setThreshold] = useState("");
  const [severity, setSeverity] = useState("high");
  const [ackRequired, setAckRequired] = useState(false);

  const create = useMutation({
    mutationFn: () =>
      api.post("/v1/admin/twin/triggers", {
        name,
        metric_key: metricKey,
        comparator,
        threshold,
        severity,
        ack_required: ackRequired,
      }),
    onSuccess: () => {
      setName("");
      setThreshold("");
      void invalidate();
    },
  });
  const toggle = useMutation({
    mutationFn: (r: TriggerRule) =>
      api.patch(`/v1/admin/twin/triggers/${r.id}`, { enabled: !r.enabled }),
    onSuccess: () => void invalidate(),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/admin/twin/triggers/${id}`),
    onSuccess: () => void invalidate(),
  });
  const evaluate = useMutation({
    mutationFn: () => api.post("/v1/admin/twin/triggers/evaluate", {}),
    onSuccess: () => void invalidate(),
  });

  const metricLabel = (key: string) =>
    (catalogue.data ?? []).find((m) => m.key === key)?.label ?? key;

  return (
    <article data-testid="triggers-view" className="max-w-4xl space-y-5">
      <header className="space-y-1">
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="telemetry"
            className="me-2 inline-block h-6 w-6 align-[-4px]"
          />
          {t("screens.triggersView.triggerRules")}
        </h1>
        <p className="max-w-3xl text-sm text-slate-500">
          {t("screens.triggersView.watchAnyCuratedEstateMetricWhenIt")}
        </p>
      </header>

      <section className="space-y-2 rounded-xl border border-paper-2 bg-white p-4 shadow-card">
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs text-slate-600">
            {t("screens.triggersView.name")}
            <input
              dir="auto"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="trigger-name"
              className="mt-1 block w-40 rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </label>
          <label className="text-xs text-slate-600">
            {t("screens.triggersView.metric")}
            <select
              value={metricKey}
              onChange={(e) => setMetricKey(e.target.value)}
              data-testid="trigger-metric"
              className="mt-1 block rounded border border-slate-300 px-2 py-1 text-sm"
            >
              {(catalogue.data ?? []).map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label} ({m.unit})
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-600">
            {t("screens.triggersView.comparator")}
            <select
              value={comparator}
              onChange={(e) => setComparator(e.target.value)}
              data-testid="trigger-comparator"
              className="mt-1 block rounded border border-slate-300 px-2 py-1 text-sm"
            >
              {Object.entries(COMPARATOR_LABEL).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-600">
            {t("screens.triggersView.threshold")}
            <input
              dir="auto"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              data-testid="trigger-threshold"
              className="mt-1 block w-24 rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </label>
          <label className="text-xs text-slate-600">
            {t("screens.triggersView.severity")}
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
              className="mt-1 block rounded border border-slate-300 px-2 py-1 text-sm"
            >
              {["critical", "high", "medium", "low"].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1 pb-1 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={ackRequired}
              onChange={(e) => setAckRequired(e.target.checked)}
            />
            {t("screens.triggersView.requireAcknowledgement")}
          </label>
          <button
            type="button"
            onClick={() => create.mutate()}
            disabled={!name || !threshold || create.isPending}
            data-testid="trigger-add"
            className="rounded bg-brand-brass px-3 py-1.5 text-sm font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
          >
            {t("screens.triggersView.addRule")}
          </button>
        </div>
      </section>

      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            {t("screens.triggersView.rules")}
          </h2>
          <button
            type="button"
            onClick={() => evaluate.mutate()}
            data-testid="trigger-evaluate"
            className="text-sm text-brand-brassDark hover:underline"
          >
            {t("screens.triggersView.evaluateNow")}
          </button>
        </div>
        {(rules.data ?? []).length === 0 ? (
          <p data-testid="triggers-empty" className="text-sm text-slate-400">
            {t("screens.triggersView.noRulesYet")}
          </p>
        ) : (
          <ul className="space-y-1">
            {(rules.data ?? []).map((r) => (
              <li
                key={r.id}
                data-testid={`trigger-row-${r.name}`}
                className={`flex flex-wrap items-baseline gap-x-3 rounded border px-3 py-2 text-sm ${
                  r.last_state === "firing"
                    ? "border-rose-200 bg-rose-50/50"
                    : "border-paper-2 bg-white"
                }`}
              >
                <span className="font-medium text-slate-900">{r.name}</span>
                <span className="text-xs text-slate-600">
                  {r.rule_kind === "event" ? (
                    t("screens.triggersView.onChange", {
                      kind: t(`modules.twin.changes.kind.${r.event_kind ?? "other"}`),
                    })
                  ) : (
                    <>
                      {metricLabel(r.metric_key ?? "")}{" "}
                      {COMPARATOR_LABEL[r.comparator ?? ""] ?? r.comparator}{" "}
                      {r.threshold}
                    </>
                  )}
                </span>
                {r.rule_kind === "event" ? (
                  <span className="text-xs text-slate-600">
                    {t("screens.triggersView.watching")}
                  </span>
                ) : r.last_state === "firing" ? (
                  <span
                    data-testid={`trigger-firing-${r.name}`}
                    className="rounded px-1.5 py-0.5 text-xs font-semibold text-rose-900 ring-1 ring-rose-300 bg-rose-100"
                  >
                    {t("screens.triggersView.firing")}
                  </span>
                ) : (
                  <span className="text-xs text-emerald-700">
                    {t("screens.triggersView.ok")}
                  </span>
                )}
                {r.last_value !== null && (
                  <span className="text-xs text-slate-500 tabular-nums">
                    {t("screens.triggersView.last")} {r.last_value}
                  </span>
                )}
                {r.ack_required && (
                  <span className="text-xs text-slate-500">
                    {t("screens.triggersView.ackRequired")}
                  </span>
                )}
                {!r.enabled && (
                  <span className="text-xs text-amber-700">
                    {t("screens.triggersView.disabled")}
                  </span>
                )}
                <span className="ms-auto flex gap-3">
                  <button
                    type="button"
                    onClick={() => toggle.mutate(r)}
                    className="text-xs text-brand-brassDark hover:underline"
                  >
                    {r.enabled
                      ? t("screens.triggersView.disable")
                      : t("screens.triggersView.enable")}
                  </button>
                  <button
                    type="button"
                    onClick={() => remove.mutate(r.id)}
                    className="text-xs text-red-600 hover:underline"
                  >
                    {t("screens.triggersView.delete")}
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </article>
  );
}
